import React from "react";

import { useTranslation } from "utils";

import { CarbonProgram } from "models";

import { AlertBanner, Button, Collapse, Flex, MainPage, StepItemCard, StickyFooterButtons, Typography } from "components";

interface FootprintingOnboardingStepsProps {
  onBegin: () => void;
  carbonProgram?: CarbonProgram;
}

const BASE_STEP_COUNT = 4;

const FootprintingOnboardingSteps: React.FC<FootprintingOnboardingStepsProps> = ({ onBegin, carbonProgram }) => {
  const { t } = useTranslation(["footprinting"]);
  const showPayouts = carbonProgram?.platformFeatures?.footprinting?.payouts ?? true;

  const stepCount = showPayouts ? BASE_STEP_COUNT + 1 : BASE_STEP_COUNT;
  const steps = Array.from({ length: stepCount }, (_, i) => ({
    title: t(`onboarding.step${i + 1}Title`),
    description: t(`onboarding.step${i + 1}Description`),
  }));

  const mainContent = (
    <Flex vertical gap={24}>
      <Flex vertical gap={16}>
        <Typography variant="h2">{t("onboarding.learnHowItWorksTitle")}</Typography>
        <Typography variant="paragraph-m">
          {t("onboarding.learnHowItWorksDescription", {
            payoutText: showPayouts ? t("onboarding.payoutText") : "",
          })}
        </Typography>
      </Flex>
      <Collapse
        className="no-padding-collapse"
        defaultActiveKey={["guide"]}
        items={[
          {
            key: "guide",
            label: (
              <Flex align="center" className="padding-left-0 padding-top-100 padding-bottom-100">
                <Typography variant="h3">👉 {t("onboarding.stepHeadline")}</Typography>
              </Flex>
            ),
            children: (
              <Flex vertical gap={24} className="padding-top-100 padding-bottom-150">
                <Flex vertical gap={24}>
                  {steps.map((step, idx) => (
                    <StepItemCard key={idx} stepNumber={idx + 1} title={step.title} description={step.description} />
                  ))}
                </Flex>
                <AlertBanner
                  title={t("onboarding.needHelpHeadline")}
                  headingSize="paragraph-s"
                  isTitleBold={true}
                  description={t("onboarding.needHelpDescription")}
                  descriptionSize="paragraph-s"
                  type="recommended"
                  gap={4}
                  descriptionColor="grey-600"
                />
              </Flex>
            ),
          },
        ]}
      />

      <Collapse
        className="no-padding-collapse"
        defaultActiveKey={["guide"]}
        items={[
          {
            key: "guide",
            label: (
              <Typography variant="h3" className="padding-top-100 padding-bottom-100">
                💡 {t("onboarding.howDoWeCalculateResults")}
              </Typography>
            ),
            children: (
              <Flex className="padding-top-100 padding-bottom-200">
                <Typography variant="paragraph-m">{t("onboarding.howDoWeCalculateResultsDescription")}</Typography>
              </Flex>
            ),
          },
        ]}
      />
    </Flex>
  );

  return (
    <>
      <MainPage pageName="FootprintingOnboarding">{mainContent}</MainPage>
      <StickyFooterButtons absolute>
        <Button type="primary" size="large" block onClick={onBegin} data-testid="footprinting-onboarding-begin">
          {t("onboarding.letsBegin")}
        </Button>
      </StickyFooterButtons>
    </>
  );
};

export default FootprintingOnboardingSteps;
